import { createFileRoute, Link } from "@tanstack/react-router";
import { Reveal } from "@/components/common/reveal";
import { BreadcrumbNav } from "@/components/common/breadcrumb-nav";
import { useLiveCategories } from "@/features/categories/hooks/use-live-categories";
import { useLiveProducts } from "@/features/products/hooks/use-live-products";
import { siteConfig } from "@/config/site";
import { collections as collectionDefs } from "@/config/collections";
import greenwall from "@/assets/greenwall.jpg";

export const Route = createFileRoute("/collections/")({
  head: () => ({
    meta: [
      // "Lookbook" and "Botanicals" were the previous wording. Both are boutique words with
      // effectively no search demand, and this is the page a category query should land on,
      // so the title now carries the terms people actually type. The brand voice is kept --
      // it just moved to the subheading in the body, where it reads to a human without
      // costing the page its match.
      { title: `Artificial Flowers & Plants Collections — ${siteConfig.name}` },
      {
        name: "description",
        content:
          "Browse artificial bonsai, 5 FT trees & plants, flower bushes, hanging floral vines, potted flower plants, and flower bunches — luxury artificial botanicals for home decoration.",
      },
      {
        property: "og:title",
        content: `Artificial Plants & Floral Collections — ${siteConfig.shortName}`,
      },
      {
        property: "og:description",
        content:
          "Artificial bonsai, 5 FT trees, flower bushes, hanging vines, flower bunches, and potted botanicals.",
      },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.url}/collections` }],
  }),
  component: CollectionsPage,
});

function CollectionsPage() {
  const { categories } = useLiveCategories();
  const { products } = useLiveProducts();

  const activeCollections = categories
    .filter((c) => (c.status || "ACTIVE") === "ACTIVE")
    .map((c) => {
      const catName = c.name || "Botanical";
      const matchingProducts = products.filter(
        (p) => (p.category || "").toLowerCase().trim() === catName.toLowerCase().trim(),
      );
      // A category browsed through /shop?category=... is not an indexable destination: the
      // prerender filter drops query URLs and a crawler reads it as a facet of /shop. Where
      // this category has a landing page in src/config/collections.ts, link there instead so
      // the internal link points at a real page. Categories created later in the admin have
      // no landing page yet and keep the /shop fallback -- browsable, just not indexed.
      const def = collectionDefs.find(
        (d) => d.category.toLowerCase().trim() === catName.toLowerCase().trim(),
      );
      return {
        title: catName,
        heading: def?.heading || catName,
        slug: def?.slug,
        count: matchingProducts.length,
        image: c.image || matchingProducts[0]?.image || "/plantpicture/p1/5.webp",
        description:
          c.description ||
          def?.subheading ||
          "Selected for scale, silhouette and the way each leaf catches low afternoon light.",
      };
    });

  return (
    <div className="pb-28 pt-24 md:pt-28">
      <div className="section-container">
        <Reveal>
          <p className="eyebrow text-xs font-semibold uppercase tracking-wider text-emerald-800">
            Collections
          </p>
          <h1 className="mt-6 max-w-3xl font-display text-4xl md:text-6xl font-bold text-foreground">
            Artificial Flowers &amp; Plants for Home Decoration
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Four collections, arranged the way rooms are lived in — flowering bushes, sculpted
            bonsai, clipped topiary and indoor foliage. Every piece is between 18 and 29 cm and
            arrives already potted.
          </p>
        </Reveal>
      </div>

      <div className="section-container mt-20 space-y-24">
        {activeCollections.map((c, i) => (
          <Reveal key={c.title}>
            <article
              className={`grid gap-8 lg:grid-cols-12 lg:items-center ${
                i % 2 ? "" : "lg:[direction:rtl]"
              }`}
            >
              <div className="lg:col-span-7 lg:[direction:ltr]">
                <img
                  src={c.image}
                  alt={c.title}
                  loading="lazy"
                  width={1400}
                  height={1000}
                  className="aspect-[16/11] w-full object-cover rounded-xl shadow-sm"
                />
              </div>
              <div className="lg:col-span-4 lg:col-start-9 lg:[direction:ltr]">
                <p className="eyebrow text-xs font-semibold uppercase tracking-wider text-emerald-800">
                  {c.count} pieces
                </p>
                <h2 className="mt-3 font-display text-2xl md:text-4xl font-bold text-foreground">
                  {c.heading}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {c.description}
                </p>
                {c.slug ? (
                  <Link
                    to="/collections/$slug"
                    params={{ slug: c.slug }}
                    className="rule-link mt-6 inline-block text-base font-semibold text-emerald-800 hover:text-emerald-900"
                  >
                    Shop {c.heading.toLowerCase()}
                  </Link>
                ) : (
                  <Link
                    to="/shop"
                    search={{ category: c.title }}
                    className="rule-link mt-6 inline-block text-base font-semibold text-emerald-800 hover:text-emerald-900"
                  >
                    Shop {c.title.toLowerCase()}
                  </Link>
                )}
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <section className="relative mt-28">
        <img
          src={greenwall}
          alt="Moss wall installation"
          loading="lazy"
          width={1400}
          height={1000}
          className="h-[50vh] w-full object-cover md:h-[70vh]"
        />
        <div className="absolute inset-0 bg-foreground/25" />
        <div className="absolute inset-0 section-container flex items-end pb-14 md:pb-20">
          <p className="max-w-xl font-display text-[clamp(1.5rem,3vw,2.6rem)] leading-tight text-background">
            Custom green walls, measured and installed.
          </p>
        </div>
      </section>
    </div>
  );
}
